import React from 'react'
import { Badge, Button, Card, CardContent, CardHeader, Table, Tbody, Td, Th, Thead, Tr } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getOwnerContactsByAccountIds } from '@/modules/accounts/services/ownerContactService'
import { fetchRewardsBingxInvitedUsers, type RewardsBingxInvitedUser } from '../../services/rewardsBingxAdminService'

function formatDate(ms: number | null): string {
  if (!ms) return '-'
  try {
    return new Date(ms).toLocaleString('pt-BR')
  } catch {
    return String(ms)
  }
}

function boolBadge(v: boolean) {
  return v ? (
    <Badge variant="soft" color="success">
      sim
    </Badge>
  ) : (
    <Badge variant="soft" color="secondary">
      nao
    </Badge>
  )
}

function normalizeErrorMessage(e: any): string {
  const raw = String(e?.message || '').trim() || 'Falha ao carregar indicados da BingX'
  if (raw.toLowerCase().includes('failed to send a request to the edge function')) {
    return 'Falha ao chamar a Edge Function. Verifique se ela foi deployada no Supabase (Functions) e se o projeto esta acessivel.'
  }
  return raw
}

export default function RewardsSettingsBingxInvitedUsersCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [rows, setRows] = React.useState<RewardsBingxInvitedUser[]>([])
  const [ownerByAccount, setOwnerByAccount] = React.useState<Map<string, any>>(new Map())
  const [search, setSearch] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(
    async (opts?: { silent?: boolean }) => {
      setError(null)
      setLoading(true)
      try {
        const resp = await fetchRewardsBingxInvitedUsers({ limit: 2000, pageSize: 200, budgetMs: 55_000 })
        setRows(resp.items || [])
        const accountIds = Array.from(new Set((resp.items || []).map((r) => r.linked_account_id).filter(Boolean) as string[]))
        if (accountIds.length === 0) {
          setOwnerByAccount(new Map())
        } else {
          const owners = await getOwnerContactsByAccountIds(accountIds)
          setOwnerByAccount(owners as any)
        }
      } catch (e: any) {
        const msg = normalizeErrorMessage(e)
        setError(msg)
        if (!opts?.silent) toast({ title: 'Rewards - BingX', description: msg, variant: 'error' })
      } finally {
        setLoading(false)
      }
    },
    [toast],
  )

  React.useEffect(() => {
    load({ silent: true })
  }, [load])

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const uid = String(r.uid)
      const inv = String(r.invitation_code || '')
      const acc = String(r.linked_account_id || '')
      const owner = r.linked_account_id ? (ownerByAccount as any).get(r.linked_account_id) : null
      const email = String(owner?.email || '').toLowerCase()
      const name = String(owner?.name || '').toLowerCase()
      return uid.includes(q) || inv.toLowerCase().includes(q) || acc.toLowerCase().includes(q) || email.includes(q) || name.includes(q)
    })
  }, [rows, search, ownerByAccount])

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 text-base font-semibold">
        <span>Rewards - BingX: indicados (MASTER)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => load()} disabled={loading}>
            Recarregar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground/70">
          Lista de usuarios indicados no programa da BingX (via API Agent). A coluna &quot;Cripto Alert&quot; indica se o UID ja foi
          vinculado por algum cliente no modulo Rewards.
        </p>

        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <input
            className="h-9 w-full max-w-[420px] rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por UID, account_id, e-mail..."
          />
          <Badge variant="soft" color="info">
            {filtered.length} itens
          </Badge>
        </div>

        {loading ? (
          <div className="text-sm text-foreground/70">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">Sem dados.</div>
        ) : (
          <div className="overflow-auto rounded-md border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>UID</Th>
                  <Th>Cadastro</Th>
                  <Th>Direto</Th>
                  <Th>Depositou</Th>
                  <Th>Trade</Th>
                  <Th>Cripto Alert</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((r) => {
                  const linked = !!r.linked_account_id
                  const owner = linked ? (ownerByAccount as any).get(r.linked_account_id) : null
                  const label = owner?.email || owner?.name || r.linked_account_id || '-'
                  return (
                    <Tr key={r.uid}>
                      <Td className="font-mono text-xs">{r.uid}</Td>
                      <Td className="text-xs">{formatDate(r.register_time_ms)}</Td>
                      <Td>{boolBadge(r.direct_invitation)}</Td>
                      <Td>{boolBadge(r.deposit)}</Td>
                      <Td>{boolBadge(r.trade)}</Td>
                      <Td>
                        {linked ? (
                          <div className="min-w-[220px]">
                            <Badge variant="soft" color="success">
                              vinculado
                            </Badge>
                            <div className="mt-1 text-xs text-foreground/70">{label}</div>
                            <div className="text-[11px] text-foreground/50">{r.linked_account_id}</div>
                          </div>
                        ) : (
                          <Badge variant="soft" color="secondary">
                            nao vinculado
                          </Badge>
                        )}
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
